import { useState, useEffect, useMemo, useRef } from 'react';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { createPortal } from 'react-dom';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { getClientDb } from '../firebase/clientApp';
import AppLayout from '../components/AppLayout';
import { DateRangeButton, IconExportSm } from '../components/HeaderControls';
import { useAuth } from '../context/AuthContext';
import { canAccessPage, COST_MARGIN_KEY } from '../utils/pages';
import { useTheme } from '../context/ThemeContext';
import { Button, Modal, StatCard } from '../components/ui';
import { EntityCombobox } from '../components/ui/EntityCombobox';
import { useSettings } from '../hooks/useSettings';
import { useLedgerData } from '../hooks/useLedgerData';
import { isIndian } from '../utils/tradingSystem';
import { gstMonths, gstBalances } from '../utils/gstSummary';
import { fmtMoney } from '../utils/currency';
import { saleRevenueOf } from '../utils/saleExpenses';
import { dueGroupOutstanding } from '../utils/dueGroups';
import { computeDateWindow, inWindow, type DateFilterKey, type DateWindow } from '../utils/ledgerReports';
import type { Entity, SaleInvoice, PurchaseInvoice, InventoryItem, TransactionCategory, PurchaseExpense, DueGroup } from '../types';

// ── Helpers (duplicated per-page, matching this codebase's convention) ──────

function round2(n: number) { return Math.round((n + Number.EPSILON) * 100) / 100; }

function outstandingOf(inv: { totalAmountUSD: number; paidUSD?: number; creditedUSD?: number }) {
  return Math.max(0, inv.totalAmountUSD - (inv.paidUSD ?? 0) - (inv.creditedUSD ?? 0));
}

// Broker commission an invoice carries — the legacy header broker (older
// docs) plus every per-line Broker1/Broker2, same math as Brokers.tsx.
function brokerCommissionOf(inv: {
  brokerEntityId?: string; brokerCommissionUSD?: number;
  lineItems?: { amountUSD: number; grossAmountUSD?: number; broker1EntityId?: string; broker1CommissionPercent?: number; broker2EntityId?: string; broker2CommissionPercent?: number }[];
}): number {
  let t = inv.brokerEntityId ? (inv.brokerCommissionUSD ?? 0) : 0;
  for (const li of inv.lineItems ?? []) {
    if (li.broker1EntityId && li.broker1CommissionPercent) t += round2((li.grossAmountUSD ?? li.amountUSD ?? 0) * li.broker1CommissionPercent / 100);
    if (li.broker2EntityId && li.broker2CommissionPercent) t += round2((li.grossAmountUSD ?? li.amountUSD ?? 0) * li.broker2CommissionPercent / 100);
  }
  return round2(t);
}
// Aging rows — a 'separate'-mode invoice ages each of its due dates on its
// own (that's what it's for); everything else ages as one lump.
function agingRows<T extends { id: string; dueDate: string; totalAmountUSD: number; paidUSD?: number; creditedUSD?: number; dueDateMode?: string; dueGroups?: DueGroup[] }>(
  invoices: T[],
): (T & { rowKey: string; outstanding: number; days: number; rowDueDate: string })[] {
  return invoices.flatMap(inv => {
    if (inv.dueDateMode === 'separate' && inv.dueGroups && inv.dueGroups.length > 0) {
      return inv.dueGroups.map(g => ({
        ...inv, rowKey: `${inv.id}_${g.id}`, outstanding: dueGroupOutstanding(g), days: daysFromToday(g.dueDate), rowDueDate: g.dueDate,
      }));
    }
    return [{ ...inv, rowKey: inv.id, outstanding: outstandingOf(inv), days: daysFromToday(inv.dueDate), rowDueDate: inv.dueDate }];
  }).filter(r => r.outstanding > 0.005);
}

function todayISO() { return new Date().toISOString().slice(0, 10); }

// Reports always show the "Real" view (no Real|Books toggle here, same
// choice already made for the Dashboard) — an invoice qualifies as long as
// it has at least one non-books-dummy line, same "any line qualifies" rule
// Purchase/Inventory/Sales' own Real filter uses.
function isRealInvoice(lineItems?: { category?: TransactionCategory }[]): boolean {
  return (lineItems ?? []).some(li => (li.category ?? 'books-real') !== 'books-dummy');
}

function daysFromToday(dateStr: string): number {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const due   = new Date(dateStr + 'T00:00:00');
  return Math.floor((today.getTime() - due.getTime()) / 86_400_000);
}

// `Date.now()` lives inside this plain helper (not inlined in a component's
// render/useMemo body) so it reads as a pure computation to the stricter
// React-Compiler-oriented lint rules — same reasoning `daysFromToday` above
// already relies on.
function daysSince(v: unknown): number {
  const then = toMillis(v);
  if (!then) return 0;
  return Math.max(0, Math.floor((Date.now() - then) / 86_400_000));
}

// Invoice numbers always show as just their last 4 digits in a pill, full
// value in a title tooltip — same convention as every other list page
// (Sales.tsx/Purchase.tsx's lotNoDisplay/LotPill).
function lotNoDisplay(invoiceNo: string) {
  if (!invoiceNo) return '—';
  const m = invoiceNo.match(/^([A-Za-z]+)-\d+-(\d+)$/);
  return m ? `${m[1]}-${m[2].slice(-4)}` : invoiceNo.slice(-4);
}
function InvoicePill({ invoiceNo }: { invoiceNo: string }) {
  if (!invoiceNo) return <span style={{ opacity: 0.4, fontSize: 12 }}>—</span>;
  return (
    <span
      title={invoiceNo}
      style={{
        display: 'inline-block', background: 'var(--primary-light)', color: 'var(--primary)',
        fontFamily: 'var(--font-num)', fontSize: 11, fontWeight: 700,
        padding: '3px 8px', borderRadius: 6, letterSpacing: '0.02em',
      }}
    >
      {lotNoDisplay(invoiceNo)}
    </span>
  );
}

function fmtDate(dateStr?: string | null) {
  if (!dateStr) return '—';
  const d = new Date(dateStr + 'T00:00:00');
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function monthLabel(key: string) {
  const [y, m] = key.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
}

/** Every month the chosen range covers ("2026-08", "2026-09", …), so the
 *  chart shows empty months too and visibly changes with the range. For
 *  All Time it starts at the earliest dated record. Capped at 24 months. */
function monthsInWindow(w: DateWindow, dates: string[]): string[] {
  const now = new Date();
  const thisMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const earliest = dates.filter(Boolean).sort()[0]?.slice(0, 7) ?? thisMonth;
  const start = (w.from ?? earliest).slice(0, 7);
  // `to` is exclusive (first day after the range): step back one day's month.
  let end = thisMonth;
  if (w.to) {
    const d = new Date(w.to + 'T00:00:00'); d.setDate(d.getDate() - 1);
    end = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    if (end > thisMonth) end = thisMonth;   // no empty future months (This Year)
  }
  const out: string[] = [];
  let [y, m] = start.split('-').map(Number);
  while (out.length < 24) {
    const key = `${y}-${String(m).padStart(2, '0')}`;
    if (key > end) break;
    out.push(key);
    m += 1; if (m > 12) { m = 1; y += 1; }
  }
  return out.length > 0 ? out : [end];
}

/** "1 Aug 2026 to 31 Oct 2026" for the chosen range. */
function windowLabel(w: DateWindow, dates: string[]): string {
  const f = (iso: string) => new Date(iso + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  const from = w.from ?? dates.filter(Boolean).sort()[0];
  let to = 'today';
  if (w.to) {
    // `to` is exclusive: show the last day inside the range (local date).
    const d = new Date(w.to + 'T00:00:00'); d.setDate(d.getDate() - 1);
    to = f(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
  }
  return from ? `${f(from)} to ${to}` : 'All time';
}

// Firestore's serverTimestamp() lands as a Timestamp at read time even
// though the app's own types declare createdAt as `string` — same helper
// as Dashboard.tsx, reads either shape into sortable millis.
function toMillis(v: unknown): number {
  if (!v) return 0;
  if (typeof v === 'string') return new Date(v).getTime();
  const obj = v as { toMillis?: () => number; seconds?: number };
  if (typeof obj.toMillis === 'function') return obj.toMillis();
  if (typeof obj.seconds === 'number') return obj.seconds * 1000;
  return 0;
}

const SERIES_LIGHT = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7', '#e34948'];
const SERIES_DARK  = ['#3987e5', '#d95926', '#199e70', '#c98500', '#d55181', '#008300', '#9085e9', '#e66767'];

// ── Chart primitives — no charting library in this repo (deliberate house
// rule, see Dashboard.tsx's Sales Performance card): plain divs for bars
// and progress fills (mirroring Dashboard's own technique exactly), plain
// inline SVG for line/pie since there's no precedent to copy for those two
// but they're standard, dependency-free techniques. ──────────────────────

interface ChartDatum { label: string; value: number; color?: string }

function MiniBarChart({ data, height = 150, money }: {
  data: ChartDatum[]; height?: number; money?: (v: number) => string;
}) {
  const [hover, setHover] = useState<number | null>(null);
  if (data.length === 0) {
    return <div style={{ fontSize: 12, color: 'var(--text-3)', padding: '20px 0' }}>No data for this period.</div>;
  }
  const max = Math.max(1, ...data.map(d => d.value));
  const fmt = (v: number) => (money ? money(v) : v.toLocaleString());
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, height, padding: '0 2px' }} onMouseLeave={() => setHover(null)}>
      {data.map((d, i) => {
        const pct = Math.max((d.value / max) * 100, d.value > 0 ? 3 : 1);
        const on = hover === i;
        return (
          <div key={d.label} onMouseEnter={() => setHover(i)}
            style={{ flex: 1, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', gap: 5, minWidth: 0, position: 'relative', cursor: 'pointer' }}>
            {/* Value only on hover, in a small tooltip above the bar. */}
            {on && (
              <div className="rep-tip" style={{ left: '50%', top: `calc(${(100 - pct) * 0.85}% - 4px)` }}>
                <span className="rep-tip-label">{d.label}</span>
                <strong>{fmt(d.value)}</strong>
              </div>
            )}
            <div style={{
              width: '100%', maxWidth: 44, height: `${pct}%`, borderRadius: '4px 4px 0 0',
              background: d.color || 'var(--primary)', transition: 'height 0.3s ease, opacity 0.12s',
              opacity: hover === null || on ? 1 : 0.45,
            }} />
            <span style={{ fontSize: 10, fontWeight: 600, color: on ? 'var(--text-1)' : 'var(--text-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>
              {d.label}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function MiniLineChart({ data, height = 140, money }: {
  data: ChartDatum[]; height?: number; money?: (v: number) => string;
}) {
  // Drawn at the box's real pixel width (measured), so dots stay round and
  // each point sits right above its month label. One point per equal-width
  // column, centred, so a single month shows as one centred dot.
  const boxRef = useRef<HTMLDivElement>(null);
  const [w, setW] = useState(0);
  const [hover, setHover] = useState<number | null>(null);
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => setW(Math.floor(entries[0].contentRect.width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  if (data.length === 0) {
    return <div style={{ fontSize: 12, color: 'var(--text-3)', padding: '20px 0' }}>No data for this period.</div>;
  }
  const H = height, TOP = 18, BOTTOM = 6;
  const max = Math.max(...data.map(d => d.value), 1);
  const cell = w > 0 ? w / data.length : 0;
  const pts = data.map((d, i) => ({
    ...d,
    x: cell * (i + 0.5),
    y: TOP + (1 - d.value / max) * (H - TOP - BOTTOM),
  }));
  const line = pts.map(p => `${p.x},${p.y}`).join(' ');
  const area = pts.length > 1 ? `${pts[0].x},${H - BOTTOM} ${line} ${pts[pts.length - 1].x},${H - BOTTOM}` : '';
  const best = pts.reduce((b, p) => (p.value > b.value ? p : b), pts[0]);
  const hot = hover !== null ? pts[hover] : null;
  const fmt = (v: number) => (money ? money(v) : v.toLocaleString());

  return (
    <div ref={boxRef} style={{ position: 'relative' }} onMouseLeave={() => setHover(null)}>
      {/* Values show only on hover, in a small tooltip above the point. */}
      {hot && (
        <div className="rep-tip" style={{ left: hot.x, top: hot.y - 10 }}>
          <span className="rep-tip-label">{hot.label}</span>
          <strong>{fmt(hot.value)}</strong>
        </div>
      )}
      {w > 0 && (
        <svg width={w} height={H} style={{ display: 'block', overflow: 'visible' }}>
          {/* faint guide lines at the top, middle and bottom */}
          {[TOP, (TOP + H - BOTTOM) / 2, H - BOTTOM].map(y => (
            <line key={y} x1={0} x2={w} y1={y} y2={y} stroke="var(--border)" strokeWidth={1} strokeDasharray={y === H - BOTTOM ? undefined : '3 4'} />
          ))}
          {area && <polygon points={area} fill="var(--primary)" opacity={0.08} />}
          {pts.length > 1 && <polyline points={line} fill="none" stroke="var(--primary)" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />}
          {hot && <line x1={hot.x} x2={hot.x} y1={TOP - 4} y2={H - BOTTOM} stroke="var(--primary)" strokeWidth={1} opacity={0.35} />}
          {pts.map((p, i) => (
            <circle key={p.label} cx={p.x} cy={p.y} r={hover === i ? 5.5 : p === best ? 4.5 : 3.5}
              fill={hover === i ? 'var(--primary)' : 'var(--surface)'} stroke="var(--primary)" strokeWidth={2} />
          ))}
          {/* One invisible hover strip per month (easier to hit than the dot). */}
          {pts.map((p, i) => (
            <rect key={`h-${p.label}`} x={cell * i} y={0} width={cell} height={H} fill="transparent"
              onMouseEnter={() => setHover(i)} style={{ cursor: 'pointer' }} />
          ))}
        </svg>
      )}
      <div style={{ display: 'flex', marginTop: 6 }}>
        {data.map((d, i) => (
          <div key={d.label} style={{ flex: 1, textAlign: 'center', minWidth: 0 }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: hover === i ? 'var(--primary)' : 'var(--text-3)' }}>{d.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function polarPoint(cx: number, cy: number, r: number, angleDeg: number) {
  const rad = (angleDeg - 90) * (Math.PI / 180);
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function MiniPieChart({ data, size = 128 }: { data: ChartDatum[]; size?: number }) {
  const total = data.reduce((s, d) => s + d.value, 0);
  if (total <= 0) {
    return <div style={{ fontSize: 12, color: 'var(--text-3)', padding: '20px 0' }}>No data for this period.</div>;
  }
  const r = size / 2;
  // Cumulative start angle per slice — a running-total reduce (no mutable
  // outer `let`) rather than a hand-incremented accumulator.
  const withData = data.filter(d => d.value > 0);
  const startAngles = withData.reduce<number[]>((acc, _d, i) => {
    const prev = i === 0 ? 0 : acc[i - 1] + (withData[i - 1].value / total) * 360;
    acc.push(prev);
    return acc;
  }, []);
  const segments = withData.map((d, i) => {
    const startAngle = startAngles[i];
    const sweep = (d.value / total) * 360;
    const endAngle = startAngle + sweep;
    const p1 = polarPoint(r, r, r, startAngle);
    const p2 = polarPoint(r, r, r, endAngle);
    const largeArc = sweep > 180 ? 1 : 0;
    // A single arc command can't sweep a full 360° (start === end has no
    // defined direction), and the previous "nudge the endpoint by half a
    // pixel" workaround left a start/end pair that wasn't actually
    // reachable at the declared radius — SVG then silently rescales the
    // arc to fit, drawing a warped shape instead of a circle. Two exact
    // semicircle arcs (each spanning a full diameter, always a valid
    // chord for the given radius) reliably draws a complete circle instead.
    const path = sweep >= 359.99
      ? `M 0,${r} A ${r},${r} 0 1,1 ${size},${r} A ${r},${r} 0 1,1 0,${r} Z`
      : `M ${r},${r} L ${p1.x},${p1.y} A ${r},${r} 0 ${largeArc},1 ${p2.x},${p2.y} Z`;
    return { ...d, path, pct: (d.value / total) * 100 };
  });
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ flexShrink: 0 }}>
        {segments.map(s => <path key={s.label} d={s.path} fill={s.color} />)}
      </svg>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
        {segments.map(s => (
          <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
            <span style={{ width: 10, height: 10, borderRadius: 3, background: s.color, flexShrink: 0 }} />
            <span style={{ color: 'var(--text-2)' }}>{s.label}</span>
            <span style={{ marginLeft: 'auto', fontFamily: 'var(--font-num)', color: 'var(--text-3)', paddingLeft: 14, whiteSpace: 'nowrap' }}>{s.pct.toFixed(0)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ProgressBar({ pct, color, label, sub }: {
  pct: number; color: string; label: React.ReactNode; sub?: React.ReactNode;
}) {
  const clamped = Math.min(100, Math.max(0, pct));
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 12, marginBottom: 4, gap: 8 }}>
        <span style={{ fontWeight: 600, color: 'var(--text-1)' }}>{label}</span>
        <span style={{ fontFamily: 'var(--font-num)', color: 'var(--text-2)', whiteSpace: 'nowrap' }}>{sub}</span>
      </div>
      <div style={{ height: 8, borderRadius: 4, background: 'var(--surface-2)', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${clamped}%`, background: color, borderRadius: 4, transition: 'width 0.3s ease' }} />
      </div>
    </div>
  );
}

// ── GST Summary (Indian trading system) ──────────────────────────────────
// Straight from the books: Output GST (charged on sales, and owed on reverse
// charge) less Input GST (paid on purchases, expenses and brokerage that we
// can claim) = what is owed to the government; Bank › Pay GST pays it.
// GST is never profit or loss.

function GstSummaryReport({ money }: { money: (v: number) => string }) {
  const { entries, loading } = useLedgerData();
  const rows = useMemo(() => gstMonths(entries).slice().reverse(), [entries]);
  const bal = useMemo(() => gstBalances(entries), [entries]);
  const monthLabel = (m: string) => new Date(`${m}-01T00:00:00`).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
  if (loading) return <EmptyCard text="Loading the books…" />;
  if (rows.length === 0) return <EmptyCard text="No GST yet. It appears here once an invoice, expense or brokerage with GST is saved." />;
  const owed = bal.net;
  return (
    <>
      <div className="rep-figures">
        <div><span>Output GST now</span><strong>{money(bal.output)}</strong></div>
        <div><span>Input GST now</span><strong>{money(bal.input)}</strong></div>
        <div><span>{owed >= 0 ? 'To pay' : 'Credit to carry'}</span><strong style={{ color: owed > 0.005 ? 'var(--danger)' : 'var(--success)' }}>{money(Math.abs(owed))}</strong></div>
      </div>
      <ReportSection title="By Month">
        <div className="table-wrap">
          <table>
            <thead><tr>
              <th>Month</th>
              <th style={{ textAlign: 'right' }} title="GST charged on sales (and owed on reverse charge), less returns">Output GST</th>
              <th style={{ textAlign: 'right' }} title="GST paid on purchases, expenses and brokerage that can be claimed">Input GST</th>
              <th style={{ textAlign: 'right' }} title="Output GST less Input GST for the month">Net</th>
              <th style={{ textAlign: 'right' }} title="Paid to the government (Bank › Pay GST)">Paid</th>
              <th style={{ textAlign: 'right' }} title="At the end of the month: owed to the government, or credit carried forward">Balance</th>
            </tr></thead>
            <tbody>
              {rows.map(r => {
                const net = Math.round((r.output - r.input) * 100) / 100;
                return (
                  <tr key={r.month}>
                    <td style={{ fontSize: 12, color: 'var(--text-2)' }}>{monthLabel(r.month)}</td>
                    <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)', textAlign: 'right' }}>{money(r.output)}</td>
                    <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)', textAlign: 'right' }}>{money(r.input)}</td>
                    <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)', textAlign: 'right' }}>{money(net)}</td>
                    <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)', textAlign: 'right' }}>{r.paid ? money(r.paid) : <span style={{ opacity: 0.4 }}>—</span>}</td>
                    <td style={{ fontSize: 12, fontFamily: 'var(--font-num)', textAlign: 'right', color: r.balance > 0.005 ? 'var(--danger)' : 'var(--text-2)' }}>
                      {r.balance >= 0 ? `${money(r.balance)} owed` : `${money(-r.balance)} credit`}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </ReportSection>
    </>
  );
}

// ── Shared layout bits ────────────────────────────────────────────────────

function ReportSection({ title, children }: { title?: string; children: React.ReactNode }) {
  return (
    <div className="rep-block">
      {title && <div className="rep-block-title">{title}</div>}
      {children}
    </div>
  );
}

function EmptyCard({ text }: { text: string }) {
  return (
    <div className="rep-block" style={{ textAlign: 'center', padding: '44px 24px', color: 'var(--text-3)', fontSize: 13 }}>
      {text}
    </div>
  );
}

const TWO_COL: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 10 };

// ── 1. Stock Valuation ────────────────────────────────────────────────────

function StockValuationReport({ inventory, money, seriesColors }: {
  inventory: InventoryItem[]; money: (v: number) => string; seriesColors: string[];
}) {
  const active = useMemo(() => inventory.filter(i => i.status !== 'written-off' && i.status !== 'merged'), [inventory]);

  const statusData = useMemo(() => {
    let onHand = 0, onMemo = 0;
    active.forEach(i => {
      const committed = i.memoCommittedCarat ?? 0;
      const perCt = i.carat > 0 ? (i.costingUSD ?? 0) / i.carat : 0;
      onMemo += perCt * committed;
      onHand += perCt * Math.max(0, i.carat - committed);
    });
    return [
      { label: 'Available',  value: round2(onHand), color: 'var(--success)' },
      { label: 'On Memo',    value: round2(onMemo),  color: 'var(--warning)' },
    ];
  }, [active]);

  const byShape = useMemo(() => {
    const m = new Map<string, number>();
    active.forEach(i => m.set(i.shape, (m.get(i.shape) ?? 0) + (i.costingUSD ?? 0)));
    return [...m.entries()]
      .map(([label, value], idx) => ({ label, value: round2(value), color: seriesColors[idx % seriesColors.length] }))
      .sort((a, b) => b.value - a.value).slice(0, 8);
  }, [active, seriesColors]);

  const grouped = useMemo(() => {
    const m = new Map<string, { shape: string; color: string; clarity: string; count: number; carat: number; value: number }>();
    active.forEach(i => {
      const key = `${i.shape}|${i.color}|${i.clarity}`;
      const g = m.get(key) ?? { shape: i.shape, color: i.color, clarity: i.clarity, count: 0, carat: 0, value: 0 };
      g.count += 1; g.carat += i.carat; g.value += (i.costingUSD ?? 0);
      m.set(key, g);
    });
    return [...m.values()].map(g => ({ ...g, carat: round2(g.carat), value: round2(g.value) })).sort((a, b) => b.value - a.value);
  }, [active]);

  const totals = useMemo(() => ({
    items: active.length,
    carat: round2(active.reduce((s, i) => s + i.carat, 0)),
    value: round2(active.reduce((s, i) => s + (i.costingUSD ?? 0), 0)),
  }), [active]);

  if (active.length === 0) return <EmptyCard text="Nothing in stock yet." />;

  return (
    <>
      <div className="stat-row">
        <StatCard label="Items in Stock" value={String(totals.items)} />
        <StatCard label="Total Carat" value={`${totals.carat.toFixed(2)} ct`} />
        <StatCard label="Total Value" value={money(totals.value)} />
      </div>
      <div style={TWO_COL}>
        <ReportSection title="Available vs On Memo"><MiniPieChart data={statusData} /></ReportSection>
        <ReportSection title="Value by Shape"><MiniBarChart data={byShape} money={money} /></ReportSection>
      </div>
      <ReportSection title="Stock Summary — by Shape / Color / Clarity">
        <div className="table-wrap">
          <table>
            <thead><tr><th>Shape</th><th>Color</th><th>Clarity</th><th>Parcels</th><th>Carat</th><th>Value</th></tr></thead>
            <tbody>
              {grouped.map(g => (
                <tr key={`${g.shape}|${g.color}|${g.clarity}`}>
                  <td style={{ fontSize: 12, color: 'var(--text-2)' }}>{g.shape}</td>
                  <td style={{ fontSize: 12, color: 'var(--text-2)' }}>{g.color}</td>
                  <td style={{ fontSize: 12, color: 'var(--text-2)' }}>{g.clarity}</td>
                  <td style={{ fontSize: 12, color: 'var(--text-2)' }}>{g.count}</td>
                  <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)' }}>{g.carat.toFixed(2)}</td>
                  <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)' }}>{money(g.value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </ReportSection>
    </>
  );
}

// ── 2. Stock Aging ────────────────────────────────────────────────────────

const AGE_BUCKETS = ['0–30d', '31–60d', '61–90d', '90d+'] as const;
function stockBucket(days: number): typeof AGE_BUCKETS[number] {
  if (days <= 30) return AGE_BUCKETS[0];
  if (days <= 60) return AGE_BUCKETS[1];
  if (days <= 90) return AGE_BUCKETS[2];
  return AGE_BUCKETS[3];
}

// A stone's age counts from the day we got it, not the day it was typed in:
// its purchase invoice's date, the opening stock date, or (for a piece cut
// off another parcel) its parent's date. Only then the record's own date.
function stockSince(
  item: InventoryItem, byId: Map<string, InventoryItem>,
  purchaseDate: Map<string, string>, openingDate: Map<string, string>,
): unknown {
  let cur: InventoryItem | undefined = item;
  for (let hop = 0; cur && hop < 20; hop++) {
    const p = purchaseDate.get(cur.purchaseId);
    if (p) return `${p}T00:00:00`;
    const o = openingDate.get(cur.id);
    if (o) return `${o}T00:00:00`;
    cur = cur.splitFromId ? byId.get(cur.splitFromId) : undefined;
  }
  return item.createdAt;
}

function StockAgingReport({ inventory, purchases, openingBatches, money }: {
  inventory: InventoryItem[]; purchases: PurchaseInvoice[];
  openingBatches: { date?: string; inventoryItemIds?: string[] }[];
  money: (v: number) => string;
}) {
  const rows = useMemo(() => {
    const byId = new Map(inventory.map(i => [i.id, i]));
    const purchaseDate = new Map(purchases.filter(p => p.purchaseDate).map(p => [p.id, p.purchaseDate as string]));
    const openingDate = new Map<string, string>();
    for (const b of openingBatches) if (b.date) for (const id of b.inventoryItemIds ?? []) openingDate.set(id, b.date);
    return inventory
      .filter(i => i.status === 'in-stock')
      .map(i => {
        const days = daysSince(stockSince(i, byId, purchaseDate, openingDate));
        return { ...i, days, bucket: stockBucket(days) };
      });
  }, [inventory, purchases, openingBatches]);

  const chartData = useMemo(() => AGE_BUCKETS.map(b => ({
    label: b,
    value: round2(rows.filter(r => r.bucket === b).reduce((s, r) => s + (r.costingUSD ?? 0), 0)),
    color: b === '90d+' ? 'var(--danger)' : b === '61–90d' ? 'var(--warning)' : 'var(--primary)',
  })), [rows]);

  const summary = useMemo(() => AGE_BUCKETS.map(b => {
    const bucketRows = rows.filter(r => r.bucket === b);
    return {
      bucket: b, count: bucketRows.length,
      carat: round2(bucketRows.reduce((s, r) => s + r.carat, 0)),
      value: round2(bucketRows.reduce((s, r) => s + (r.costingUSD ?? 0), 0)),
    };
  }), [rows]);

  if (rows.length === 0) return <EmptyCard text="Nothing in stock yet." />;

  return (
    <>
      <ReportSection title="Stock Value by Age"><MiniBarChart data={chartData} money={money} /></ReportSection>
      <ReportSection title="Aging Summary">
        <div className="table-wrap">
          <table>
            <thead><tr><th>Age</th><th>Parcels</th><th>Carat</th><th>Value</th></tr></thead>
            <tbody>
              {summary.map(s => (
                <tr key={s.bucket}>
                  <td style={{ fontSize: 12, color: s.bucket === '90d+' ? 'var(--danger)' : 'var(--text-2)', fontWeight: s.bucket === '90d+' ? 700 : 400 }}>{s.bucket}</td>
                  <td style={{ fontSize: 12, color: 'var(--text-2)' }}>{s.count}</td>
                  <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)' }}>{s.carat.toFixed(2)}</td>
                  <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)' }}>{money(s.value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </ReportSection>
    </>
  );
}

// ── 3/4. Sales / Purchase Summary ─────────────────────────────────────────

// Small colour dot before a company name, matching its Top Customers /
// Top Suppliers bar. Companies outside the top five get a grey one.
function CompanyDot({ color }: { color?: string }) {
  return (
    <span style={{
      display: 'inline-block', width: 8, height: 8, borderRadius: '50%', marginRight: 7,
      verticalAlign: 'middle', background: color ?? 'var(--border)',
    }} />
  );
}

function SalesSummaryReport({ sales, entities, money, window, seriesColors }: {
  sales: SaleInvoice[]; entities: Entity[]; money: (v: number) => string; window: DateWindow; seriesColors: string[];
}) {
  const entityName = (id: string) => entities.find(e => e.id === id)?.companyName ?? '—';
  const filtered = useMemo(() => sales.filter(s => inWindow(s.saleDate, window)), [sales, window]);

  const byMonth = useMemo(() => {
    const m = new Map<string, number>();
    filtered.forEach(s => { const k = s.saleDate.slice(0, 7); m.set(k, (m.get(k) ?? 0) + saleRevenueOf(s)); });
    return monthsInWindow(window, sales.map(s => s.saleDate)).map(key => ({ label: monthLabel(key), value: round2(m.get(key) ?? 0) }));
  }, [filtered, window, sales]);
  const periodText = windowLabel(window, sales.map(s => s.saleDate));

  // Each top company gets its own colour, used for its bar and its dot in
  // the Invoices table so the two are easy to match.
  const byCustomer = useMemo(() => {
    const m = new Map<string, number>();
    filtered.forEach(s => m.set(s.toEntityId, (m.get(s.toEntityId) ?? 0) + saleRevenueOf(s)));
    return [...m.entries()]
      .sort((a, b) => b[1] - a[1]).slice(0, 5)
      .map(([id, value], idx) => ({ id, label: entityName(id), value: round2(value), color: seriesColors[idx % seriesColors.length] }));
  }, [filtered, entities, seriesColors]); // eslint-disable-line react-hooks/exhaustive-deps
  const colorOf = (id: string) => byCustomer.find(c => c.id === id)?.color;

  const totals = useMemo(() => ({
    invoices: filtered.length,
    carat: round2(filtered.reduce((s, x) => s + x.totalCarat, 0)),
    revenue: round2(filtered.reduce((s, x) => s + saleRevenueOf(x), 0)),
    margin: round2(filtered.reduce((s, x) => s + (x.totalMarginUSD ?? 0), 0)),
    // Selling costs that come off the margin: broker commission + our own
    // sale-side expenses (shipping/insurance we bear). Charges re-billed to
    // the customer are neither revenue nor cost, so they're left out.
    brokers: round2(filtered.reduce((s, x) => s + brokerCommissionOf(x), 0)),
    selling: round2(filtered.reduce((s, x) => s + (x.sellingExpensesUSD ?? 0), 0)),
  }), [filtered]);
  const netMargin = round2(totals.margin - totals.brokers - totals.selling);

  if (filtered.length === 0) return <EmptyCard text={`No sales from ${periodText}.`} />;

  return (
    <>
      <div className="rep-period">Showing {periodText}</div>
      <div className="stat-row rep-figures">
        <StatCard label="Invoices" value={String(totals.invoices)} />
        <StatCard label="Total Carat" value={`${totals.carat.toFixed(2)} ct`} />
        <StatCard label="Revenue" value={money(totals.revenue)} />
        <StatCard label="Gross Margin" value={money(totals.margin)} valueColor={totals.margin >= 0 ? 'var(--success)' : 'var(--danger)'} />
        <StatCard label="Broker Commission" value={money(totals.brokers)} />
        <StatCard label="Selling Expenses" value={money(totals.selling)} sub="shipping, insurance… we paid" />
        <StatCard label="Net Margin" value={money(netMargin)} valueColor={netMargin >= 0 ? 'var(--success)' : 'var(--danger)'} />
      </div>
      {/* Charts are month-bucketed — a handful of points at most, so they
          stay a fixed, readable width instead of stretching edge-to-edge
          on a wide screen for no reason. */}
      <div style={TWO_COL}>
        <ReportSection title="Revenue by Month"><MiniLineChart data={byMonth} money={money} /></ReportSection>
        <ReportSection title="Top Customers"><MiniBarChart data={byCustomer} money={money} /></ReportSection>
      </div>
      <ReportSection title="Invoices">
        <div className="table-wrap">
          <table>
            <thead><tr><th>Date</th><th>Invoice No</th><th>Customer</th><th>Carat</th><th>Revenue</th><th>Charges</th><th>Invoice Total</th></tr></thead>
            <tbody>
              {filtered.slice().sort((a, b) => b.saleDate.localeCompare(a.saleDate)).map(s => (
                <tr key={s.id}>
                  <td style={{ fontSize: 12, color: 'var(--text-2)' }}>{fmtDate(s.saleDate)}</td>
                  <td><InvoicePill invoiceNo={s.invoiceNo} /></td>
                  <td style={{ fontSize: 12, color: 'var(--text-2)' }}><CompanyDot color={colorOf(s.toEntityId)} />{entityName(s.toEntityId)}</td>
                  <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)' }}>{s.totalCarat.toFixed(2)}</td>
                  <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)' }}>{money(saleRevenueOf(s))}</td>
                  <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)' }}>{s.chargesUSD ? money(s.chargesUSD) : <span style={{ opacity: 0.4 }}>—</span>}</td>
                  <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)' }}>{money(s.totalAmountUSD)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </ReportSection>
    </>
  );
}

function PurchaseSummaryReport({ purchases, purchaseExpenses, entities, money, window, seriesColors }: {
  purchases: PurchaseInvoice[]; purchaseExpenses: PurchaseExpense[]; entities: Entity[]; money: (v: number) => string; window: DateWindow; seriesColors: string[];
}) {
  const entityName = (id: string) => entities.find(e => e.id === id)?.companyName ?? '—';
  const filtered = useMemo(() => purchases.filter(p => inWindow(p.purchaseDate ?? '', window)), [purchases, window]);

  const byMonth = useMemo(() => {
    const m = new Map<string, number>();
    filtered.forEach(p => { const k = (p.purchaseDate ?? '').slice(0, 7); if (!k) return; m.set(k, (m.get(k) ?? 0) + p.totalAmountUSD - (p.taxTotalUSD ?? 0)); });
    return monthsInWindow(window, purchases.map(p => p.purchaseDate ?? '')).map(key => ({ label: monthLabel(key), value: round2(m.get(key) ?? 0) }));
  }, [filtered, window, purchases]);
  const periodText = windowLabel(window, purchases.map(p => p.purchaseDate ?? ''));

  // Each top company gets its own colour, used for its bar and its dot in
  // the Invoices table so the two are easy to match.
  const bySupplier = useMemo(() => {
    const m = new Map<string, number>();
    filtered.forEach(p => m.set(p.fromEntityId, (m.get(p.fromEntityId) ?? 0) + p.totalAmountUSD - (p.taxTotalUSD ?? 0)));
    return [...m.entries()]
      .sort((a, b) => b[1] - a[1]).slice(0, 5)
      .map(([id, value], idx) => ({ id, label: entityName(id), value: round2(value), color: seriesColors[idx % seriesColors.length] }));
  }, [filtered, entities, seriesColors]); // eslint-disable-line react-hooks/exhaustive-deps
  const colorOf = (id: string) => bySupplier.find(c => c.id === id)?.color;

  const totals = useMemo(() => ({
    invoices: filtered.length,
    carat: round2(filtered.reduce((s, x) => s + x.totalCarat, 0)),
    spend: round2(filtered.reduce((s, x) => s + x.totalAmountUSD - (x.taxTotalUSD ?? 0), 0)),   // without Indian GST (claimed back)
    brokers: round2(filtered.reduce((s, x) => s + brokerCommissionOf(x), 0)),
  }), [filtered]);
  // Freight / duty / certification dated in this period (Purchase Expenses
  // page + each invoice's Additional Details), split by how it was booked.
  const pe = useMemo(() => {
    const inW = purchaseExpenses.filter(e => inWindow(e.date, window));
    return {
      capitalised: round2(inW.filter(e => e.treatment === 'capitalise').reduce((s, e) => s + (e.amountUSD ?? 0), 0)),
      expensed:    round2(inW.filter(e => e.treatment !== 'capitalise').reduce((s, e) => s + (e.amountUSD ?? 0), 0)),
    };
  }, [purchaseExpenses, window]);
  const landed = round2(totals.spend + pe.capitalised + pe.expensed + totals.brokers);

  if (filtered.length === 0) return <EmptyCard text={`No purchases from ${periodText}.`} />;

  return (
    <>
      <div className="rep-period">Showing {periodText}</div>
      <div className="stat-row rep-figures">
        <StatCard label="Invoices" value={String(totals.invoices)} />
        <StatCard label="Total Carat" value={`${totals.carat.toFixed(2)} ct`} />
        <StatCard label="Total Spend" value={money(totals.spend)} sub="stones, per invoices" />
        <StatCard label="Purchase Expenses" value={money(pe.capitalised + pe.expensed)}
          sub={`${money(pe.capitalised)} into stock · ${money(pe.expensed)} expensed`} />
        <StatCard label="Broker Commission" value={money(totals.brokers)} />
        <StatCard label="Total Landed Cost" value={money(landed)} />
      </div>
      <div style={TWO_COL}>
        <ReportSection title="Spend by Month"><MiniLineChart data={byMonth} money={money} /></ReportSection>
        <ReportSection title="Top Suppliers"><MiniBarChart data={bySupplier} money={money} /></ReportSection>
      </div>
      <ReportSection title="Invoices">
        <div className="table-wrap">
          <table>
            <thead><tr><th>Date</th><th>Invoice No</th><th>Supplier</th><th>Carat</th><th>Amount</th></tr></thead>
            <tbody>
              {filtered.slice().sort((a, b) => (b.purchaseDate ?? '').localeCompare(a.purchaseDate ?? '')).map(p => (
                <tr key={p.id}>
                  <td style={{ fontSize: 12, color: 'var(--text-2)' }}>{fmtDate(p.purchaseDate)}</td>
                  <td><InvoicePill invoiceNo={p.invoiceNo} /></td>
                  <td style={{ fontSize: 12, color: 'var(--text-2)' }}><CompanyDot color={colorOf(p.fromEntityId)} />{entityName(p.fromEntityId)}</td>
                  <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)' }}>{p.totalCarat.toFixed(2)}</td>
                  <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)' }}>{money(p.totalAmountUSD)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </ReportSection>
    </>
  );
}

// ── 5/6. AR / AP Aging ────────────────────────────────────────────────────

const AR_BUCKETS = ['Current', '1–30d', '31–60d', '61–90d', '90d+'] as const;
function overdueBucket(days: number): typeof AR_BUCKETS[number] {
  if (days <= 0)  return 'Current';
  if (days <= 30) return '1–30d';
  if (days <= 60) return '31–60d';
  if (days <= 90) return '61–90d';
  return '90d+';
}
function bucketColor(b: string) {
  if (b === '90d+') return 'var(--danger)';
  if (b === '61–90d') return 'var(--warning)';
  if (b === 'Current') return 'var(--success)';
  return 'var(--primary)';
}

function ARAgingReport({ sales, entities, money }: { sales: SaleInvoice[]; entities: Entity[]; money: (v: number) => string }) {
  const entityName = (id: string) => entities.find(e => e.id === id)?.companyName ?? '—';
  const rows = useMemo(() => agingRows(sales).map(r => ({ ...r, bucket: overdueBucket(r.days) })), [sales]);

  const chartData = useMemo(() => AR_BUCKETS.map(b => ({
    label: b, value: round2(rows.filter(r => r.bucket === b).reduce((s, r) => s + r.outstanding, 0)), color: bucketColor(b),
  })), [rows]);
  const pieData = useMemo(() => chartData.filter(d => d.value > 0), [chartData]);
  const total = round2(rows.reduce((s, r) => s + r.outstanding, 0));

  if (rows.length === 0) return <EmptyCard text="Nothing outstanding — all customers are paid up." />;

  return (
    <>
      <div className="stat-row">
        <StatCard label="Open Invoices" value={String(new Set(rows.map(r => r.id)).size)} />
        <StatCard label="Total Outstanding" value={money(total)} valueColor="var(--danger)" />
      </div>
      <div style={TWO_COL}>
        <ReportSection title="Outstanding by Age"><MiniBarChart data={chartData} money={money} /></ReportSection>
        <ReportSection title="Share by Age"><MiniPieChart data={pieData} /></ReportSection>
      </div>
      <ReportSection title="Open Invoices">
        <div className="table-wrap">
          <table>
            <thead><tr><th>Customer</th><th>Invoice No</th><th>Due Date</th><th>Status</th><th>Outstanding</th></tr></thead>
            <tbody>
              {rows.slice().sort((a, b) => b.days - a.days).map(r => (
                <tr key={r.rowKey}>
                  <td style={{ fontSize: 12, color: 'var(--text-2)' }}>{entityName(r.toEntityId)}</td>
                  <td><InvoicePill invoiceNo={r.invoiceNo} /></td>
                  <td style={{ fontSize: 12, color: 'var(--text-2)' }}>{fmtDate(r.rowDueDate)}</td>
                  <td style={{ fontSize: 12, color: r.days > 0 ? 'var(--danger)' : 'var(--success)', fontWeight: 600 }}>
                    {r.days > 0 ? `${r.days}d overdue` : 'Current'}
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)' }}>{money(r.outstanding)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </ReportSection>
    </>
  );
}

function APAgingReport({ purchases, entities, money }: { purchases: PurchaseInvoice[]; entities: Entity[]; money: (v: number) => string }) {
  const entityName = (id: string) => entities.find(e => e.id === id)?.companyName ?? '—';
  const rows = useMemo(() => agingRows(purchases).map(r => ({ ...r, bucket: overdueBucket(r.days) })), [purchases]);

  const chartData = useMemo(() => AR_BUCKETS.map(b => ({
    label: b, value: round2(rows.filter(r => r.bucket === b).reduce((s, r) => s + r.outstanding, 0)), color: bucketColor(b),
  })), [rows]);
  const pieData = useMemo(() => chartData.filter(d => d.value > 0), [chartData]);
  const total = round2(rows.reduce((s, r) => s + r.outstanding, 0));

  if (rows.length === 0) return <EmptyCard text="Nothing outstanding — all suppliers are paid up." />;

  return (
    <>
      <div className="stat-row">
        <StatCard label="Open Invoices" value={String(new Set(rows.map(r => r.id)).size)} />
        <StatCard label="Total Outstanding" value={money(total)} valueColor="var(--danger)" />
      </div>
      <div style={TWO_COL}>
        <ReportSection title="Outstanding by Age"><MiniBarChart data={chartData} money={money} /></ReportSection>
        <ReportSection title="Share by Age"><MiniPieChart data={pieData} /></ReportSection>
      </div>
      <ReportSection title="Open Invoices">
        <div className="table-wrap">
          <table>
            <thead><tr><th>Supplier</th><th>Invoice No</th><th>Due Date</th><th>Status</th><th>Outstanding</th></tr></thead>
            <tbody>
              {rows.slice().sort((a, b) => b.days - a.days).map(r => (
                <tr key={r.rowKey}>
                  <td style={{ fontSize: 12, color: 'var(--text-2)' }}>{entityName(r.fromEntityId)}</td>
                  <td><InvoicePill invoiceNo={r.invoiceNo} /></td>
                  <td style={{ fontSize: 12, color: 'var(--text-2)' }}>{fmtDate(r.rowDueDate)}</td>
                  <td style={{ fontSize: 12, color: r.days > 0 ? 'var(--danger)' : 'var(--success)', fontWeight: 600 }}>
                    {r.days > 0 ? `${r.days}d overdue` : 'Current'}
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)' }}>{money(r.outstanding)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </ReportSection>
    </>
  );
}

// ── 7/8. Customer / Supplier Statement ────────────────────────────────────

// The picked company lives on the page (`entityId`) so Export PDF can
// still print this statement while another report is on screen.
function CustomerStatementReport({ sales, customers, money, titleSlot, entityId, setEntityId }: {
  sales: SaleInvoice[]; customers: Entity[]; money: (v: number) => string; titleSlot: HTMLElement | null;
  entityId: string; setEntityId: (id: string) => void;
}) {
  const entity = customers.find(e => e.id === entityId);
  const rows = useMemo(() => {
    const list = sales.filter(s => s.toEntityId === entityId).slice().sort((a, b) => a.saleDate.localeCompare(b.saleDate));
    return list.reduce<Array<SaleInvoice & { outstanding: number; running: number }>>((acc, s) => {
      const outstanding = outstandingOf(s);
      const running = round2((acc.length ? acc[acc.length - 1].running : 0) + outstanding);
      acc.push({ ...s, outstanding, running });
      return acc;
    }, []);
  }, [sales, entityId]);

  return (
    <>
      {/* The customer picker sits on the report's title row. */}
      {titleSlot && createPortal(
        <div className="form-group tb-combo">
          <EntityCombobox entities={customers} value={entityId} onChange={setEntityId} placeholder="Select a customer…" />
        </div>,
        titleSlot,
      )}
      {!entityId ? (
        <EmptyCard text="Pick a customer at the top to see their statement." />
      ) : rows.length === 0 ? (
        <EmptyCard text={`No invoices for ${entity?.companyName ?? 'this customer'}.`} />
      ) : (
        <ReportSection title={`${entity?.companyName ?? ''} — Statement`}>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Date</th><th>Invoice No</th><th>Amount</th><th>Paid</th><th>Credited</th><th>Outstanding</th><th>Running Balance</th></tr></thead>
              <tbody>
                {rows.map(s => (
                  <tr key={s.id}>
                    <td style={{ fontSize: 12, color: 'var(--text-2)' }}>{fmtDate(s.saleDate)}</td>
                    <td><InvoicePill invoiceNo={s.invoiceNo} /></td>
                    <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)' }}>{money(s.totalAmountUSD)}</td>
                    <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)' }}>{money(s.paidUSD ?? 0)}</td>
                    <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)' }}>{s.creditedUSD ? money(s.creditedUSD) : <span style={{ opacity: 0.4 }}>—</span>}</td>
                    <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)' }}>{money(s.outstanding)}</td>
                    <td style={{ fontSize: 12, color: 'var(--text-1)', fontFamily: 'var(--font-num)', fontWeight: 700 }}>{money(s.running)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={6} style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-2)' }}>Balance Due</td>
                  <td style={{ fontSize: 12, fontFamily: 'var(--font-num)', fontWeight: 700 }}>{money(rows[rows.length - 1]?.running ?? 0)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </ReportSection>
      )}
    </>
  );
}

function SupplierStatementReport({ purchases, suppliers, money, titleSlot, entityId, setEntityId }: {
  purchases: PurchaseInvoice[]; suppliers: Entity[]; money: (v: number) => string; titleSlot: HTMLElement | null;
  entityId: string; setEntityId: (id: string) => void;
}) {
  const entity = suppliers.find(e => e.id === entityId);
  const rows = useMemo(() => {
    const list = purchases.filter(p => p.fromEntityId === entityId).slice().sort((a, b) => (a.purchaseDate ?? '').localeCompare(b.purchaseDate ?? ''));
    return list.reduce<Array<PurchaseInvoice & { outstanding: number; running: number }>>((acc, p) => {
      const outstanding = outstandingOf(p);
      const running = round2((acc.length ? acc[acc.length - 1].running : 0) + outstanding);
      acc.push({ ...p, outstanding, running });
      return acc;
    }, []);
  }, [purchases, entityId]);

  return (
    <>
      {/* The supplier picker sits on the report's title row. */}
      {titleSlot && createPortal(
        <div className="form-group tb-combo">
          <EntityCombobox entities={suppliers} value={entityId} onChange={setEntityId} placeholder="Select a supplier…" />
        </div>,
        titleSlot,
      )}
      {!entityId ? (
        <EmptyCard text="Pick a supplier at the top to see their statement." />
      ) : rows.length === 0 ? (
        <EmptyCard text={`No invoices for ${entity?.companyName ?? 'this supplier'}.`} />
      ) : (
        <ReportSection title={`${entity?.companyName ?? ''} — Statement`}>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Date</th><th>Invoice No</th><th>Amount</th><th>Paid</th><th>Credited</th><th>Outstanding</th><th>Running Balance</th></tr></thead>
              <tbody>
                {rows.map(p => (
                  <tr key={p.id}>
                    <td style={{ fontSize: 12, color: 'var(--text-2)' }}>{fmtDate(p.purchaseDate)}</td>
                    <td><InvoicePill invoiceNo={p.invoiceNo} /></td>
                    <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)' }}>{money(p.totalAmountUSD)}</td>
                    <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)' }}>{money(p.paidUSD ?? 0)}</td>
                    <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)' }}>{p.creditedUSD ? money(p.creditedUSD) : <span style={{ opacity: 0.4 }}>—</span>}</td>
                    <td style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-num)' }}>{money(p.outstanding)}</td>
                    <td style={{ fontSize: 12, color: 'var(--text-1)', fontFamily: 'var(--font-num)', fontWeight: 700 }}>{money(p.running)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={6} style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-2)' }}>Balance Due</td>
                  <td style={{ fontSize: 12, fontFamily: 'var(--font-num)', fontWeight: 700 }}>{money(rows[rows.length - 1]?.running ?? 0)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </ReportSection>
      )}
    </>
  );
}

// ── 9. Credit Limit Utilization ───────────────────────────────────────────
// Same formula Sales.tsx's SaleAddModal uses for its own credit-limit
// warning, generalized from "one customer" to every customer that has a
// creditLimitUSD set.

// `titleSlot` is a spot on the report's title row; the customer search is
// drawn there (next to "Credit Limit") instead of in a box of its own.
function CreditLimitReport({ sales, entities, money, titleSlot, filterId, setFilterId }: {
  sales: SaleInvoice[]; entities: Entity[]; money: (v: number) => string; titleSlot: HTMLElement | null;
  filterId: string; setFilterId: (id: string) => void;
}) {

  const allRows = useMemo(() => entities
    .filter(e => e.types.includes('customer') && !!e.creditLimitUSD)
    .map(e => {
      const outstanding = round2(sales.filter(s => s.toEntityId === e.id).reduce((sum, s) => sum + outstandingOf(s), 0));
      const limit = e.creditLimitUSD!;
      const pct = limit > 0 ? round2((outstanding / limit) * 100) : 0;
      return { entity: e, outstanding, limit, pct };
    })
    .sort((a, b) => b.pct - a.pct), [sales, entities]);

  // Same EntityCombobox-as-a-filter pattern Memos.tsx's "All Clients" picker
  // already uses — type to filter, pick one to narrow the list down to just
  // them (a plain substring search box would work too, but this reuses the
  // exact search/autofill control every other filter in the app already is).
  const pickable = useMemo(() => allRows.map(r => r.entity), [allRows]);
  const rows = filterId ? allRows.filter(r => r.entity.id === filterId) : allRows;

  if (allRows.length === 0) {
    return <EmptyCard text="No customers have a Credit Limit set yet — add one from Entity → Edit Entity." />;
  }

  return (
    <div style={{ maxWidth: 640 }}>
      {titleSlot && createPortal(
        <div className="form-group tb-combo">
          <EntityCombobox
            entities={pickable} value={filterId} onChange={setFilterId}
            clearLabel="All Customers" placeholder="Find customer…"
          />
        </div>,
        titleSlot,
      )}
      <ReportSection title="Credit Limit Utilization">
        {rows.length === 0 ? (
          <div style={{ fontSize: 12, color: 'var(--text-3)', padding: '20px 0' }}>No match.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            {rows.map(r => (
              <ProgressBar
                key={r.entity.id}
                pct={r.pct}
                color={r.pct >= 100 ? 'var(--danger)' : r.pct >= 70 ? 'var(--warning)' : 'var(--success)'}
                label={r.entity.companyName}
                sub={`${money(r.outstanding)} / ${money(r.limit)} · ${r.pct.toFixed(0)}%`}
              />
            ))}
          </div>
        )}
      </ReportSection>
    </div>
  );
}

// ── Export PDF helpers ────────────────────────────────────────────────────

// html2canvas 1.4 can't read modern colour functions: Chrome reports every
// `color-mix()` (used all over index.css) as `color(srgb …)`, and html2canvas
// throws "unsupported color function" on it (why Sales Summary's PDF failed).
// In the copy html2canvas renders, every such colour is turned into plain
// rgba by painting it on a 1px canvas and reading the pixel back.
const MODERN_COLOR = /(?:color|color-mix|oklch|oklab|lab|lch|hwb)\((?:[^()]|\([^()]*\))*\)/g;
const COLOR_PROPS = [
  'color', 'background-color', 'background-image', 'border-top-color', 'border-right-color',
  'border-bottom-color', 'border-left-color', 'outline-color', 'text-decoration-color',
  'box-shadow', 'fill', 'stroke', 'stop-color', 'caret-color', 'column-rule-color',
];
const rgbaCache = new Map<string, string>();
let pixelCtx: CanvasRenderingContext2D | null = null;
function toRgba(color: string): string {
  const hit = rgbaCache.get(color);
  if (hit) return hit;
  if (!pixelCtx) {
    const c = document.createElement('canvas');
    c.width = c.height = 1;
    pixelCtx = c.getContext('2d', { willReadFrequently: true });
  }
  let out = 'rgba(0, 0, 0, 0)';
  if (pixelCtx) {
    pixelCtx.clearRect(0, 0, 1, 1);
    pixelCtx.fillStyle = '#000';
    pixelCtx.fillStyle = color;
    pixelCtx.fillRect(0, 0, 1, 1);
    const [r, g, b, a] = pixelCtx.getImageData(0, 0, 1, 1).data;
    out = `rgba(${r}, ${g}, ${b}, ${Math.round((a / 255) * 1000) / 1000})`;
  }
  rgbaCache.set(color, out);
  return out;
}
function flattenColors(root: Element) {
  const view = root.ownerDocument.defaultView;
  if (!view) return;
  const els = [root, ...Array.from(root.querySelectorAll('*'))] as (HTMLElement | SVGElement)[];
  for (const el of els) {
    const cs = view.getComputedStyle(el);
    for (const prop of COLOR_PROPS) {
      const v = cs.getPropertyValue(prop);
      if (!v || !v.includes('(')) continue;
      MODERN_COLOR.lastIndex = 0;
      if (!MODERN_COLOR.test(v)) continue;
      el.style.setProperty(prop, v.replace(MODERN_COLOR, m => toRgba(m)), 'important');
    }
  }
}

// Export popup: tick the reports to put in one PDF.
function ExportReportsModal({ open, onClose, groups, current, blocked, progress, error, onExport }: {
  open: boolean; onClose: () => void;
  groups: { label: string; items: { key: string; label: string }[] }[];
  current: string;
  blocked: (key: string) => string;
  progress: { label: string; n: number; of: number } | null;
  error: string;
  onExport: (keys: string[]) => void;
}) {
  const all = groups.flatMap(g => g.items.map(i => i.key)).filter(k => !blocked(k));
  const [picked, setPicked] = useState<Set<string>>(() => new Set([current]));
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setPicked(new Set(blocked(current) ? [] : [current]));
  }
  const keys = all.filter(k => picked.has(k));
  const busy = !!progress;
  const toggle = (k: string) => setPicked(p => { const n = new Set(p); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  return (
    <Modal open={open} onClose={busy ? () => {} : onClose} title="Export Reports" size="sm"
      footer={<>
        <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={() => onExport(keys)} disabled={keys.length === 0} loading={busy}>
          {keys.length > 1 ? `Export ${keys.length} Reports` : 'Export PDF'}
        </Button>
      </>}>
      <div className="rep-export-tools">
        <span>One PDF, each report on its own pages.</span>
        <button type="button" className="rep-export-link" disabled={busy}
          onClick={() => setPicked(new Set(keys.length === all.length ? [] : all))}>
          {keys.length === all.length ? 'Clear' : 'Select all'}
        </button>
      </div>
      {groups.map(g => (
        <div key={g.label} className="rep-export-group">
          <div className="dropdown-group-label">{g.label}</div>
          {g.items.map(i => {
            const why = blocked(i.key);
            return (
              <label key={i.key} className={`dropdown-item rep-export-item${why ? ' is-off' : ''}`} title={why || undefined}>
                <input type="checkbox" checked={!why && picked.has(i.key)} disabled={!!why || busy} onChange={() => toggle(i.key)} />
                <span>{g.label === 'Statements' ? `${i.label} Statement` : i.label}</span>
                {why && <span className="rep-export-why">{why}</span>}
              </label>
            );
          })}
        </div>
      ))}
      {progress && <div className="rep-export-progress">Making PDF: {progress.label} ({progress.n} of {progress.of})</div>}
      {error && <div className="msg-error" style={{ marginTop: 10 }}>{error}</div>}
    </Modal>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────

// The left list, grouped. `page` = the page a staff member needs to see
// that report (stock value also needs the cost right).
const REPORT_GROUPS: { label: string; items: { key: string; label: string; page: string; cost?: boolean }[] }[] = [
  { label: 'Stock', items: [
    { key: 'stock-valuation', label: 'Stock Valuation', page: 'inventory', cost: true },
    { key: 'stock-aging',     label: 'Stock Aging',     page: 'inventory' },
  ] },
  { label: 'Trading', items: [
    { key: 'sales-summary',    label: 'Sales Summary',    page: 'sales' },
    { key: 'purchase-summary', label: 'Purchase Summary', page: 'purchase' },
  ] },
  { label: 'Owed', items: [
    { key: 'ar-aging',     label: 'Receivable Aging', page: 'ar' },
    { key: 'ap-aging',     label: 'Payable Aging',    page: 'ap' },
    { key: 'credit-limit', label: 'Credit Limit',     page: 'ar' },
  ] },
  { label: 'Statements', items: [
    { key: 'customer-statement', label: 'Customer', page: 'ar' },
    { key: 'supplier-statement', label: 'Supplier', page: 'ap' },
  ] },
  // Indian trading system only (filtered out for Belgian below).
  { label: 'Tax', items: [
    { key: 'gst-summary', label: 'GST Summary', page: 'bank' },
  ] },
];

// A small icon per group so the sections are easy to tell apart.
const GROUP_ICON: Record<string, React.ReactNode> = {
  Stock: <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><polygon points="4,2 12,2 14.5,6 8,14.5 1.5,6" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" /><path d="M1.5 6h13" stroke="currentColor" strokeWidth="1.3" /></svg>,
  Trading: <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><path d="M2 11l4-4 3 3 5-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /><path d="M10 4h4v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>,
  Owed: <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.4" /><path d="M8 4.5V8l2.5 1.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" /></svg>,
  Tax: <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><path d="M4 12L12 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /><circle cx="5" cy="5" r="1.6" stroke="currentColor" strokeWidth="1.3" /><circle cx="11" cy="11" r="1.6" stroke="currentColor" strokeWidth="1.3" /></svg>,
  Statements: <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><rect x="3" y="1.5" width="10" height="13" rx="1.5" stroke="currentColor" strokeWidth="1.4" /><path d="M5.5 5h5M5.5 8h5M5.5 11h3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" /></svg>,
};

const REPORTS = [
  { key: 'stock-valuation',    label: 'Stock Valuation' },
  { key: 'stock-aging',        label: 'Stock Aging' },
  { key: 'sales-summary',      label: 'Sales Summary' },
  { key: 'purchase-summary',   label: 'Purchase Summary' },
  { key: 'ar-aging',           label: 'AR Aging' },
  { key: 'ap-aging',           label: 'AP Aging' },
  { key: 'customer-statement', label: 'Customer Statement' },
  { key: 'supplier-statement', label: 'Supplier Statement' },
  { key: 'credit-limit',       label: 'Credit Limit' },
  { key: 'gst-summary',        label: 'GST Summary' },
] as const;
type ReportKey = typeof REPORTS[number]['key'];

// Reports that trend over a period get the date filter; snapshot-style
// reports (stock, aging, statements, credit limit) show everything as of
// now, matching how AP.tsx/AR.tsx's own live lists have no date filter.
const DATE_FILTERED: ReadonlySet<ReportKey> = new Set(['sales-summary', 'purchase-summary']);

export default function ReportsPage() {
  const settings = useSettings();
  const { theme } = useTheme();
  const seriesColors = theme === 'dark' ? SERIES_DARK : SERIES_LIGHT;
  const money = (v: number) => fmtMoney(v ?? 0, settings.currency);

  const [entities,  setEntities]  = useState<Entity[]>([]);
  const [sales,      setSales]      = useState<SaleInvoice[]>([]);
  const [purchases,  setPurchases]  = useState<PurchaseInvoice[]>([]);
  const [openingBatches, setOpeningBatches] = useState<{ date?: string; inventoryItemIds?: string[] }[]>([]);
  const [inventory,  setInventory]  = useState<InventoryItem[]>([]);
  const [purchaseExpenses, setPurchaseExpenses] = useState<PurchaseExpense[]>([]);
  const [loading,    setLoading]    = useState(true);

  const { user, business } = useAuth();
  // Only reports for pages this user can open (stock value also needs cost).
  const visibleGroups = REPORT_GROUPS
    .filter(g => g.label !== 'Tax' || isIndian(settings))
    .map(g => ({ ...g, items: g.items.filter(i => canAccessPage(user, i.page) && (!i.cost || canAccessPage(user, COST_MARGIN_KEY))) }))
    .filter(g => g.items.length > 0);
  const firstReport = (visibleGroups[0]?.items[0]?.key ?? 'stock-valuation') as ReportKey;
  const [pickedReport, setReportKey] = useState<ReportKey | null>(null);
  const reportKey: ReportKey = pickedReport && visibleGroups.some(g => g.items.some(i => i.key === pickedReport)) ? pickedReport : firstReport;
  const [dateKey,    setDateKey]    = useState<DateFilterKey>('this-month');
  const [titleSlot, setTitleSlot] = useState<HTMLElement | null>(null);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo,   setCustomTo]   = useState('');
  const [customerStmtId, setCustomerStmtId] = useState('');
  const [supplierStmtId, setSupplierStmtId] = useState('');
  const [creditFilterId, setCreditFilterId] = useState('');

  useEffect(() => {
    const db = getClientDb();
    if (!db) return;
    const u1 = onSnapshot(query(collection(db, 'entities'), orderBy('companyName')),
      snap => setEntities(snap.docs.map(d => ({ id: d.id, ...d.data() } as Entity))));
    const u2 = onSnapshot(collection(db, 'sales'),
      snap => { setSales(snap.docs.map(d => ({ id: d.id, ...d.data() } as SaleInvoice))); setLoading(false); },
      () => setLoading(false));
    const u3 = onSnapshot(collection(db, 'invoices'),
      snap => setPurchases(snap.docs.map(d => ({ id: d.id, ...d.data() } as PurchaseInvoice))));
    const u4 = onSnapshot(collection(db, 'inventory'),
      snap => setInventory(snap.docs.map(d => ({ id: d.id, ...d.data() } as InventoryItem))));
    const u5 = onSnapshot(collection(db, 'purchaseExpenses'),
      snap => setPurchaseExpenses(snap.docs.map(d => ({ id: d.id, ...d.data() } as PurchaseExpense))), () => {});
    // Opening stock batches carry the stock date (Stock Aging counts from it).
    const u6 = onSnapshot(collection(db, 'openingStock'),
      snap => setOpeningBatches(snap.docs.map(d => d.data() as { date?: string; inventoryItemIds?: string[] })), () => {});
    return () => { u1(); u2(); u3(); u4(); u5(); u6(); };
  }, []);

  const customers = useMemo(() => entities.filter(e => e.types.includes('customer')), [entities]);
  const suppliers = useMemo(() => entities.filter(e => e.types.includes('supplier')), [entities]);
  const dateWindow = useMemo(() => computeDateWindow(dateKey, customFrom, customTo), [dateKey, customFrom, customTo]);

  // "Real" view only — books-dummy stock/invoices are a bookkeeping
  // reconciliation mechanism, not real physical/financial activity, so they
  // never belong in a report. Filtered once here, fed to every report below.
  const realSales      = useMemo(() => sales.filter(inv => isRealInvoice(inv.lineItems)), [sales]);
  const realPurchases  = useMemo(() => purchases.filter(inv => isRealInvoice(inv.lineItems)), [purchases]);
  const realInventory  = useMemo(() => inventory.filter(i => i.category !== 'books-dummy'), [inventory]);

  // Export PDF: one file with every report ticked in the Export popup, each
  // starting on a new page with its own title. Reports are put on screen one
  // after another and captured (so each uses exactly what the screen shows:
  // the date range, the picked customer / supplier). While capturing, the
  // shell drops its one screen height and every scroll box so all rows are
  // laid out. Pages are cut under a row or between blocks, a page starting
  // inside a table repeats its header row, and each page is its own canvas
  // (one tall canvas went past the browser's size limit and came out blank).
  const contentRef = useRef<HTMLDivElement>(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportProgress, setExportProgress] = useState<{ label: string; n: number; of: number } | null>(null);
  const [exportError, setExportError] = useState('');

  // What a statement prints for: no company picked = nothing to print.
  const exportBlocked = (key: string): string => {
    if (key === 'customer-statement' && !customerStmtId) return 'Pick a customer on this report first';
    if (key === 'supplier-statement' && !supplierStmtId) return 'Pick a supplier on this report first';
    return '';
  };
  const reportTitle = (key: ReportKey): string => {
    const label = REPORTS.find(r => r.key === key)?.label ?? 'Report';
    const party = key === 'customer-statement' ? entities.find(e => e.id === customerStmtId)?.companyName
      : key === 'supplier-statement' ? entities.find(e => e.id === supplierStmtId)?.companyName
      : undefined;
    return party ? `${label}: ${party}` : label;
  };

  async function exportReports(keys: ReportKey[]) {
    const startKey = reportKey;
    const shellEl = contentRef.current?.closest('.rep-shell') as HTMLElement | null;
    const scroller = contentRef.current?.closest('.rep-content') as HTMLElement | null;
    const keepScroll = scroller?.scrollTop ?? 0;
    setExportError('');
    shellEl?.classList.add('rep-capturing');
    try {
      const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait', compress: true });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const titleTop = 22;   // room for the report name on its first page
      const footer = 8;      // room for the page number
      const usableW = pageW - margin * 2;
      const stamp = business?.businessName ? `${business.businessName} · ${fmtDate(todayISO())}` : fmtDate(todayISO());
      let pagesSoFar = 0;

      for (let k = 0; k < keys.length; k++) {
        const key = keys[k];
        setExportProgress({ label: REPORTS.find(r => r.key === key)?.label ?? '', n: k + 1, of: keys.length });
        setReportKey(key);
        const node = await waitForReport(key);
        const widthPx = Math.ceil(Math.max(node.scrollWidth, node.getBoundingClientRect().width));
        const totalPx = Math.ceil(node.scrollHeight);
        const pxPerMm = widthPx / usableW;
        const firstPagePx = (pageH - titleTop - margin - footer) * pxPerMm;
        const otherPagePx = (pageH - margin * 2 - footer) * pxPerMm;

        // Safe places to cut: under every table row, above and under every
        // block, under every top level piece.
        const top0 = node.getBoundingClientRect().top;
        const cuts = new Set<number>();
        node.querySelectorAll('tr, .rep-block, .rep-figures, .stat-row').forEach(el => {
          const r = el.getBoundingClientRect();
          cuts.add(Math.round(r.bottom - top0));
          if (!(el instanceof HTMLTableRowElement)) cuts.add(Math.round(r.top - top0));
        });
        Array.from(node.children).forEach(el => cuts.add(Math.round(el.getBoundingClientRect().bottom - top0)));
        const cutList = [...cuts].filter(y => y > 0 && y < totalPx).sort((a, b) => a - b);

        // Every table's header row, so a page that starts inside a table can
        // repeat it at the top.
        const heads = Array.from(node.querySelectorAll('table')).flatMap(t => {
          const th = t.tHead;
          if (!th) return [];
          const hr = th.getBoundingClientRect();
          return [{
            top: Math.round(hr.top - top0), bottom: Math.round(hr.bottom - top0),
            end: Math.round(t.getBoundingClientRect().bottom - top0),
          }];
        });
        const headAt = (y: number) => heads.find(h => y >= h.bottom && y < h.end - 1);

        const slices: { y: number; h: number; head?: { top: number; bottom: number } }[] = [];
        let y = 0;
        while (y < totalPx - 1) {
          const head = slices.length > 0 ? headAt(y) : undefined;
          const room = (slices.length === 0 ? firstPagePx : otherPagePx) - (head ? head.bottom - head.top : 0);
          let end = y + room;
          if (end >= totalPx) end = totalPx;
          else {
            // The lowest safe cut in the bottom half of the page, else a hard cut.
            const fit = cutList.filter(c => c > y + room * 0.5 && c <= end);
            if (fit.length) end = fit[fit.length - 1];
          }
          slices.push({ y, h: Math.ceil(end - y), head });
          y = end;
        }

        const capture = (cy: number, ch: number) => html2canvas(node, {
          scale: 2, useCORS: true, backgroundColor: '#ffffff',
          x: 0, y: cy, width: widthPx, height: ch,
          onclone: (doc: Document) => { const c = doc.querySelector('.rep-body'); if (c) flattenColors(c); },
        });
        const headImages = new Map<number, string>();
        for (let i = 0; i < slices.length; i++) {
          const sl = slices[i];
          const canvas = await capture(sl.y, sl.h);
          if (pagesSoFar > 0) pdf.addPage();
          pagesSoFar++;
          let top = i === 0 ? titleTop : margin;
          if (i === 0) {
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(14);
            pdf.setTextColor(15, 23, 42);
            pdf.text(reportTitle(key), margin, 14);
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(9);
            pdf.setTextColor(100, 116, 139);
            pdf.text(stamp, pageW - margin, 14, { align: 'right' });
          }
          if (sl.head) {
            const hh = sl.head.bottom - sl.head.top;
            let img = headImages.get(sl.head.top);
            if (!img) {
              img = (await capture(sl.head.top, hh)).toDataURL('image/jpeg', 0.92);
              headImages.set(sl.head.top, img);
            }
            pdf.addImage(img, 'JPEG', margin, top, usableW, hh / pxPerMm);
            top += hh / pxPerMm;
          }
          pdf.addImage(canvas.toDataURL('image/jpeg', 0.92), 'JPEG', margin, top, usableW, sl.h / pxPerMm);
        }
      }

      // Page numbers across the whole file.
      const total = pdf.getNumberOfPages();
      for (let i = 1; i <= total; i++) {
        pdf.setPage(i);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(8);
        pdf.setTextColor(148, 163, 184);
        pdf.text(`Page ${i} of ${total}`, pageW / 2, pageH - margin + 2, { align: 'center' });
      }
      const name = keys.length === 1 ? (REPORTS.find(r => r.key === keys[0])?.label ?? 'Report') : 'Reports';
      pdf.save(`${name.replace(/\s+/g, '-')}-${todayISO()}.pdf`);
      setExportOpen(false);
    } catch (err) {
      console.error(err);
      setExportError(`Could not make the PDF (${err instanceof Error ? err.message : String(err)}). Please try again.`);
    } finally {
      shellEl?.classList.remove('rep-capturing');
      setReportKey(startKey);
      setExportProgress(null);
      requestAnimationFrame(() => { if (scroller) scroller.scrollTop = keepScroll; });
    }
  }

  // Waits until the report `key` is on screen and laid out.
  async function waitForReport(key: ReportKey): Promise<HTMLDivElement> {
    const frame = () => new Promise(r => requestAnimationFrame(() => r(null)));
    for (let i = 0; i < 120; i++) {
      await frame();
      const node = contentRef.current;
      if (node && node.dataset.report === key) {
        await frame(); await frame();
        await new Promise(r => setTimeout(r, 120));   // charts settle
        return node;
      }
    }
    throw new Error('the report did not open');
  }

  return (
    <AppLayout
      title="Reports"
      subtitle="Business snapshots — stock, sales, purchases, and outstanding balances"
      toolbar={DATE_FILTERED.has(reportKey) ? (
        <DateRangeButton
          value={dateKey} onChange={setDateKey}
          customFrom={customFrom} customTo={customTo}
          onCustomFrom={setCustomFrom} onCustomTo={setCustomTo}
        />
      ) : undefined}
      headerActions={
        <Button variant="secondary" size="sm" icon={<IconExportSm />} onClick={() => { setExportError(''); setExportOpen(true); }} disabled={loading || visibleGroups.length === 0}
          style={{ height: 34, boxSizing: 'border-box' }}>
          Export PDF
        </Button>
      }
    >
      {/* Same shape as Settings: the report list on the left, the report on
          the right, sized to one screen (tables scroll inside their box). */}
      <div className="settings-shell rep-shell">
        <nav className="settings-nav rep-nav no-print" aria-label="Reports">
          {visibleGroups.map(g => (
            <div key={g.label} className="rep-nav-group">
              <div className="rep-nav-label">
                <span className="rep-nav-icon" aria-hidden="true">{GROUP_ICON[g.label]}</span>
                {g.label}
              </div>
              {g.items.map(r => (
                <button key={r.key} type="button"
                  className={`settings-nav-item${reportKey === r.key ? ' active' : ''}`}
                  onClick={() => setReportKey(r.key as ReportKey)}>
                  {r.label}
                </button>
              ))}
            </div>
          ))}
        </nav>
        <div className="settings-content rep-content">
          {visibleGroups.length === 0 ? (
            <EmptyCard text="No reports for the pages you can open." />
          ) : (<>
            <div className="rep-title">
              <strong className="rep-title-name">{REPORT_GROUPS.flatMap(g => g.items).find(r => r.key === reportKey)?.label}</strong>
              {/* iPad / phone: the left list is hidden, this picker takes its place
                  (the device's own list opens on tap). */}
              <select className="rep-picker no-print" aria-label="Report" value={reportKey}
                onChange={e => setReportKey(e.target.value as ReportKey)}>
                {visibleGroups.map(g => (
                  <optgroup key={g.label} label={g.label}>
                    {g.items.map(r => (
                      <option key={r.key} value={r.key}>{g.label === 'Statements' ? `${r.label} Statement` : r.label}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
              {/* A report can put its own control here (e.g. Credit Limit's customer search). */}
              <span ref={setTitleSlot} className="rep-title-slot no-print" />
            </div>
      {loading ? (
        <div className="card"><div className="skeleton" style={{ height: 240, borderRadius: 8 }} /></div>
      ) : (
        <div ref={contentRef} className="rep-body" data-report={reportKey}>
          {reportKey === 'stock-valuation' && <StockValuationReport inventory={realInventory} money={money} seriesColors={seriesColors} />}
          {reportKey === 'stock-aging' && <StockAgingReport inventory={realInventory} purchases={purchases} openingBatches={openingBatches} money={money} />}
          {reportKey === 'sales-summary' && (
            !dateWindow
              ? <EmptyCard text="Choose a From and To date above." />
              : <SalesSummaryReport sales={realSales} entities={entities} money={money} window={dateWindow} seriesColors={seriesColors} />
          )}
          {reportKey === 'purchase-summary' && (
            !dateWindow
              ? <EmptyCard text="Choose a From and To date above." />
              : <PurchaseSummaryReport purchases={realPurchases} purchaseExpenses={purchaseExpenses} entities={entities} money={money} window={dateWindow} seriesColors={seriesColors} />
          )}
          {reportKey === 'ar-aging' && <ARAgingReport sales={realSales} entities={entities} money={money} />}
          {reportKey === 'ap-aging' && <APAgingReport purchases={realPurchases} entities={entities} money={money} />}
          {reportKey === 'customer-statement' && <CustomerStatementReport sales={realSales} customers={customers} money={money} titleSlot={titleSlot} entityId={customerStmtId} setEntityId={setCustomerStmtId} />}
          {reportKey === 'gst-summary' && <GstSummaryReport money={money} />}
          {reportKey === 'supplier-statement' && <SupplierStatementReport purchases={realPurchases} suppliers={suppliers} money={money} titleSlot={titleSlot} entityId={supplierStmtId} setEntityId={setSupplierStmtId} />}
          {reportKey === 'credit-limit' && <CreditLimitReport sales={realSales} entities={entities} money={money} titleSlot={titleSlot} filterId={creditFilterId} setFilterId={setCreditFilterId} />}
        </div>
      )}
          </>)}
        </div>
      </div>
      <ExportReportsModal
        open={exportOpen} onClose={() => setExportOpen(false)}
        groups={visibleGroups} current={reportKey}
        blocked={exportBlocked} progress={exportProgress} error={exportError}
        onExport={keys => exportReports(REPORTS.map(r => r.key).filter(k => keys.includes(k)))}
      />
    </AppLayout>
  );
}
